/**
 * DeployHealthBanner — v2.5.910
 *
 * Red banner shown to admins when the backend hasn't restarted within 6h of
 * the last frontend deploy. Prevents the "silent stale bytecode" trap where
 * /api/version reports the latest string but new routes still 404.
 *
 * Compares `backend_started_at` (from /api/version) to the current frontend's
 * PLATFORM_VERSION build timestamp. If backend uptime > 6h AND frontend
 * version differs from backend version, we flag it.
 *
 * Dismissable per-session so it doesn't nag while an admin is investigating.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, X, ExternalLink } from 'lucide-react';
import { PLATFORM_VERSION } from '../config/version';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'deploy_health_banner_dismissed';
const STALE_THRESHOLD_HOURS = 6;
// v2.5.923 — Tolerate a 1-patch-version drift silently. This drift is EXPECTED
// on every deploy because auto-version bumps once more AFTER `yarn build`
// finishes (when the release-changelog `[skip ci]` commit lands). A single
// patch difference between bundle and backend is normal and self-heals on
// the next build. Only raise the red banner for MEANINGFUL drift (≥ 2 patch
// versions apart, or any major/minor difference).
function _versionsFarApart(a, b) {
  try {
    const pa = String(a).split('.').map(n => parseInt(n, 10) || 0);
    const pb = String(b).split('.').map(n => parseInt(n, 10) || 0);
    if (pa[0] !== pb[0] || pa[1] !== pb[1]) return true;              // any major/minor drift = show banner
    return Math.abs((pa[2] || 0) - (pb[2] || 0)) >= 2;                // ≥ 2 patch versions = show banner
  } catch { return true; }
}

export default function DeployHealthBanner() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');

  // Only super_admin / owner / country_operator care about deploy health.
  const shouldCheck = user && ['super_admin', 'owner', 'platform_owner', 'country_operator', 'subscriber_admin'].includes(user.role);

  useEffect(() => {
    if (!shouldCheck || dismissed) return;
    let alive = true;
    axios.get(`${API}/api/version`).then(({ data }) => {
      if (!alive) return;
      const backendStarted = data?.backend_started_at ? new Date(data.backend_started_at) : null;
      const backendVersion = data?.version || null;
      const hoursSinceRestart = backendStarted ? (Date.now() - backendStarted.getTime()) / 3_600_000 : null;
      const versionMismatch = backendVersion && _versionsFarApart(backendVersion, PLATFORM_VERSION);
      const stale = hoursSinceRestart !== null && hoursSinceRestart > STALE_THRESHOLD_HOURS && versionMismatch;
      if (stale) {
        setState({ hoursSinceRestart: Math.round(hoursSinceRestart * 10) / 10, backendVersion });
      }
    }).catch(() => { /* silent — banner is best-effort */ });
    return () => { alive = false; };
  }, [shouldCheck, dismissed]);

  if (!shouldCheck || dismissed || !state) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* storage blocked */ }
  };

  return (
    <div className="bg-red-600 text-white px-4 py-2 flex items-center gap-3 text-sm z-[100]" data-testid="deploy-health-banner">
      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold">Backend is stale</span>
        {' '}— running <span className="font-mono">{state.backendVersion}</span> while frontend is on <span className="font-mono">{PLATFORM_VERSION}</span>. Backend hasn&apos;t restarted in <b>{state.hoursSinceRestart}h</b>. New API routes may 404 until you touch <span className="font-mono">tmp/restart.txt</span> on the server.
      </div>
      <a
        href="/settings/deploy-health"
        className="underline flex items-center gap-1 font-medium hover:no-underline flex-shrink-0"
        data-testid="deploy-health-banner-link"
      >
        Open Deploy Health <ExternalLink className="h-3 w-3" />
      </a>
      <button onClick={dismiss} className="text-white/80 hover:text-white flex-shrink-0" title="Dismiss for this session" data-testid="deploy-health-banner-dismiss">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
